import React, { useEffect } from 'react'
import { debounce } from 'lodash'
import { useDispatch } from 'react-redux'
import Grid from 'grid'
import { Button, Icon } from '@material-ui/core'
import useStateWithCallback from 'hooks/useStateWithCallback'

/* grid components */
import DecimalCellEditor from 'components/grid/DecimalCellEditor'
import DatepickerGridCell from 'components/EditableGrid/components/DatepickerGridCell'

import DeleteButton from './components/DeleteButton'
import ActionButtons from './components/ActionButtons'
import SalesOrderTooltipCell from './components/SalesOrderTooltipCell'
import { handleRecurringOrderInteraction } from '../../actions'

import '../../styles/modal-flex-button-styles.scss'

const rightAlignParams = {
  headerClass: 'align-right right-align',
  cellClass: 'align-right right-align'
}

const centerAlignParams = {
  cellClass: 'text-center align-center',
  headerClass: 'text-center align-center'
}

let gridApi = null
let columnApi = null
let _isMounted = false

const RecurringLineItemsScheduleModal = props => {
  useEffect(() => {
    _isMounted = true
    return () => {
      _isMounted = false
    }
  }, [])

  const dispatch = useDispatch()

  const {
    data: {
      form,
      balance,
      isEditing,
      lineNumber,
      isEditable,
      scheduledOrders = []
    },
    id
  } = props

  const blankRow = {
    quantity: null,
    shipDate: null,
    commitDate: null,
    order: null,
    status: { value: null },
    rowId: 'blankrow',
    canChangeQuantity: isEditing,
    canChangeShipDate: isEditing,
    canChangeCommitDate: isEditing,
    canDelete: isEditing
  }

  const [state, setState] = useStateWithCallback({
    balance,
    scheduledOrders:
      scheduledOrders && Array.isArray(scheduledOrders) ? scheduledOrders : []
  })

  const beginEditingNewRow = () => {
    setTimeout(() => {
      if (_isMounted && gridApi) {
        gridApi.forEachNode(node => {
          if (
            (node?.data?.rowId === 'blankrow' && !node?.data?.quantity) ||
            (node?.data?.lineNumber === 0 && !node?.data?.quantity)
          ) {
            const { rowIndex } = node
            gridApi.setFocusedCell(rowIndex, 'quantity')
            gridApi.startEditingCell({
              rowIndex,
              colKey: 'quantity'
            })
          }
        })
      }
    }, 0)
  }

  const updateStateAfterApiTransaction = (
    result,
    bypassBlankRowCheck = false
  ) => {
    const hasBlankRow =
      !bypassBlankRowCheck &&
      state.scheduledOrders.find(x => x?.rowId && x?.rowId === 'blankrow')

    setState(
      {
        ...state,
        balance: result.balance,
        isEditable: result.isEditable,
        scheduledOrders:
          result?.scheduledOrders && Array.isArray(result?.scheduledOrders)
            ? hasBlankRow
              ? [
                  ...result.scheduledOrders,
                  {
                    lineNumber: result.scheduledOrders.length
                      ? result.scheduledOrders.length
                      : 0,
                    ...blankRow
                  }
                ]
              : result.scheduledOrders
            : state.scheduledOrders || []
      },
      () => beginEditingNewRow()
    )
  }

  const onCellValueChanged = async params => {
    if (
      ((params.newValue || params.newValue === 0) &&
        Number(params.newValue) === Number(params.oldValue)) ||
      params.newValue === params.oldValue ||
      !_isMounted
    ) {
      return
    }

    const lineNum = params?.data?.lineNumber
    const isBlankRowChange = params?.data?.rowId === 'blankrow'

    let result
    try {
      result = await dispatch(
        handleRecurringOrderInteraction.try(form, {
          lineNumber: lineNum,
          apiParams: {
            properties: {
              [params?.colDef?.colId]:
                params?.colDef?.colId === 'quantity'
                  ? Number(params.newValue)
                  : params.newValue
            }
          },
          gridApi: params.api
        })
      )
    } catch (error) {
      console.log(error)
    } finally {
      updateStateAfterApiTransaction(result, isBlankRowChange)
    }
  }

  const onClickDeleteButton = async (e, lineItemData) => {
    if (lineItemData?.rowId && lineItemData.rowId === 'blankrow') {
      await setState({
        ...state,
        scheduledOrders: state.scheduledOrders.filter(
          x => x?.rowId !== 'blankrow'
        )
      })

      return
    }

    let result
    try {
      result = await dispatch(
        handleRecurringOrderInteraction.try(form, {
          lineNumber: lineItemData.lineNumber,
          apiParams: {
            properties: {
              delete: lineItemData.lineNumber
            }
          }
        })
      )
    } catch (error) {
      console.log(error)
    } finally {
      updateStateAfterApiTransaction(result)
    }
  }

  const onClickLoadScheduleButton = async e => {
    let result
    try {
      result = await dispatch(
        handleRecurringOrderInteraction.try(form, {
          apiParams: {
            properties: {
              loadSchedule: true
            }
          }
        })
      )
    } catch (error) {
      console.log(error)
    } finally {
      updateStateAfterApiTransaction(result, true)
    }
  }

  const onCellFocused = params => {
    if (
      !params.column ||
      (params.column && params.column.colDef && !params.column.colDef.editable)
    ) {
      return
    }

    if (params?.column?.colId) {
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.column && params.column.colId
      })
    }
  }

  const columnDefs = [
    {
      field: 'quantity',
      colId: 'quantity',
      headerName: 'Qty',
      cellEditor: 'decimalCellEditor',
      editable: params => params?.data?.canChangeQuantity,
      onCellValueChanged,
      onCellFocused,
      minWidth: 125,
      ...rightAlignParams
    },
    {
      field: 'shipDate',
      colId: 'shipDate',
      headerName: 'Ship Date',
      cellRendererFramework: DatepickerGridCell,
      cellRendererParams: {
        form,
        disabledFn: data => !data.canChangeShipDate
      },
      onCellValueChanged,
      editable: false,
      suppressNavigable: true,
      minWidth: 150
    },
    {
      field: 'commitDate',
      colId: 'commitDate',
      headerName: 'Commit Date',
      cellRendererFramework: DatepickerGridCell,
      cellRendererParams: {
        form,
        disabledFn: data => !data.canChangeCommitDate
      },
      onCellValueChanged,
      editable: false,
      suppressNavigable: true,
      minWidth: 150
    },
    {
      field: 'dataId',
      colId: 'dataId',
      cellRendererFramework: SalesOrderTooltipCell,
      headerName: 'Order',
      editable: false,
      minWidth: 100
    },
    {
      field: 'status.value',
      colId: 'status.value',
      headerName: 'Status',
      editable: false,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      filterParams: {
        clearButton: true,
        suppressAndOrCondition: true
      },
      tooltipField: 'status.tooltip'
    },
    {
      field: 'lineNumber',
      colId: 'lineNumber',
      headerName: '',
      cellRendererFramework: DeleteButton,
      cellRendererParams: {
        form,
        onClickDeleteButton
      },
      minWidth: 50,
      maxWidth: 50
    }
  ]

  const addBlankRow = debounce(e => {
    if (
      state.scheduledOrders &&
      Array.isArray(state.scheduledOrders) &&
      state.scheduledOrders?.every(x => x.quantity)
    ) {
      setState(
        {
          ...state,
          scheduledOrders: [
            ...state.scheduledOrders,
            {
              lineNumber: state.scheduledOrders.length
                ? state.scheduledOrders.length
                : 0,
              ...blankRow
            }
          ]
        },
        () => beginEditingNewRow()
      )
    } else if (
      state.scheduledOrders &&
      Array.isArray(state.scheduledOrders) &&
      !state.scheduledOrders.length
    ) {
      setState(
        {
          ...state,
          scheduledOrders: [
            {
              lineNumber: 0,
              ...blankRow
            }
          ]
        },
        () => beginEditingNewRow()
      )
    } else if (!state.scheduledOrders) {
      setState(
        {
          ...state,
          scheduledOrders: [
            {
              lineNumber: 0,
              ...blankRow
            }
          ]
        },
        () => beginEditingNewRow()
      )
    }
  }, 200)

  const onGridReady = params => {
    gridApi = params.api
    columnApi = params.columnApi
  }

  return (
    <div className="sales-order-entry-fullscreen-flex-modal-wrapper">
      <div
        className="sales-order-entry-modal-primary-interface-wrapper"
        style={{ paddingBottom: 30 }}
      >
        <div style={{ width: '100%' }}>
          <Grid
            headerName="Line Item Schedule"
            columnDefs={columnDefs}
            rowData={
              Array.isArray(state.scheduledOrders) ? state.scheduledOrders : []
            }
            getRowNodeById={data => data.lineNumber}
            addBlankRow={addBlankRow}
            height={250}
            onGridReady={onGridReady}
            components={{
              decimalCellEditor: DecimalCellEditor
            }}
            singleClickEdit
          />
          <div style={{ display: isEditable ? 'block' : 'none' }}>
            <Button
              variant="contained"
              onClick={addBlankRow}
              size="small"
              color="secondary"
              tabIndex={-1}
              disabled={!isEditable}
            >
              <Icon style={{ fontSize: 16, marginRight: 5 }}>add_circle</Icon>
              <span>Add Scheduled Order</span>
            </Button>
          </div>
        </div>
      </div>
      <ActionButtons
        form={form}
        modalId={id}
        balance={state.balance}
        lineNumber={lineNumber}
        isEditable={isEditable}
        onClickLoadScheduleButton={onClickLoadScheduleButton}
      />
    </div>
  )
}

export default RecurringLineItemsScheduleModal
